import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Centered,
  CardBox,
  PrimaryButton,
  AlternativeButton,
  InlineParagraph,
  Toolbar,
  MoreDetailsPanel,
  SplitPanel,
  TextArea
} from "../CardsStyled";
import CalendarIcon from "../../Icons/CalendarIcon";
import CardCore from "../CardCore";
import ErrorComponent from "../Error";
import { useAppointmentConfirmation } from "../hooks/useAppointmentConfirmation";
import { useTranslate } from "hooks/translation-hooks";
import AddCalendar from "components/AddCalendar";
import { errorSelector } from "redux/selectors/error";

const appointmentConfirmationRequiredCategory =
  "appointment_confirmation_required";

export const AppointmentConfirmationRequired = ({
  description,
  jobReference,
  missionOrderId,
  appointmentProposalStart,
  appointmentProposalEnd,
  providerName
}) => {
  const t = useTranslate();
  const {
    moreInformation,
    updateMoreInfo,
    confirmCurrentAppointment,
    declineCurrentAppointment
  } = useAppointmentConfirmation({
    jobReference,
    missionOrderId
  });
  const error = useSelector(errorSelector);
  if (error) {
    return (
      <ErrorComponent
        description="contact_us_card-appointment_confirmation_text_1"
        dataCategory="appointment-confirmation-error-page"
        title="contact_us_card-appointment_confirmation_text_1"
      />
    );
  }
  const isAppointmentInTheFuture =
    new Date(appointmentProposalStart) > new Date(Date.now());

  return (
    <CardBox
      data-testid="appointment_card"
      data-category={appointmentConfirmationRequiredCategory}
    >
      <SplitPanel>
        <Centered>
          <CalendarIcon />
        </Centered>
        <CardCore
          title={t("appointment_card-title")}
          additionalDescription={`${t(
            "appointment_card-text_1"
          )} ${providerName} ${t("appointment_card-text_2")}`}
          description={description}
          rawEventDate={appointmentProposalStart}
          appointmentProposalStart={appointmentProposalStart}
          appointmentProposalEnd={appointmentProposalEnd}
        />
        {isAppointmentInTheFuture && <AddCalendar />}
        <MoreDetailsPanel>
          <InlineParagraph>{t("more_information_card-title")}</InlineParagraph>
          <TextArea
            data-testid="appointment_more_information"
            placeholder={t("more_information_card-textbox_1")}
            onInput={updateMoreInfo}
            value={moreInformation}
          />
        </MoreDetailsPanel>
        <Toolbar>
          <PrimaryButton
            disabled={!moreInformation}
            data-action="accept_button-click"
            data-testid="accept_button-click"
            onClick={confirmCurrentAppointment}
          >
            {t("appointment_card-button_1")}
          </PrimaryButton>
          <AlternativeButton
            data-action="decline_button-click"
            data-testid="decline_button-click"
            onClick={declineCurrentAppointment}
          >
            {t("appointment_card-button_2")}
          </AlternativeButton>
        </Toolbar>
      </SplitPanel>
    </CardBox>
  );
};

AppointmentConfirmationRequired.propTypes = {
  missionOrderId: PropTypes.string.isRequired,
  jobReference: PropTypes.string.isRequired,
  rawEventDate: PropTypes.string,
  description: PropTypes.string,
  isCurrent: PropTypes.bool.isRequired,
  providerName: PropTypes.string.isRequired
};

AppointmentConfirmationRequired.defaultProps = {
  rawEventDate: "2039-05-29T00:00:00.000Z",
  description: "Between 4pm and 6pm",
  error: null
};

export default AppointmentConfirmationRequired;
